<template>
  <div class="result-image" :class="{ 'result-image--mobile': isMobile }">
    <div  v-if="!isEdit" class="result-image__content">
      <div class="result-image__inner">
        <div class="result-image__tabs" v-if="imageForActions !== ''">
          <ai-tabs v-model="imageTab" :items="imageTabs" type="square" />
        </div>
        <div class="result-image__box" :class="{ 'mt-8': imageForActions === '' }">
          <div class="result-image__box__img" :style="{ 'aspect-ratio': imageWidth / imageHeight }">
            <ImgComparisonSlider
              v-show="imageTab === 'comparison'"
              class="result-image__box__slider"
              :style="{ 'aspect-ratio': imageWidth / imageHeight }"
            >
              <!-- eslint-disable -->
              <img
                slot="first"
                class="first"
                style="width: 100%; box-sizing: border-box"
                :src="imageForActions"
                alt=""
              />
              <img slot="second" class="second" style="width: 100%" :src="imageResult" alt="" />

              <div class="handle" slot="handle">
                <inline-svg :src="require('@/assets/icons/slider.svg')" />
              </div>
              <!-- eslint-enable -->
            </ImgComparisonSlider>
            <v-img
              v-show="imageTab === 'after'"
              :src="imageResult"
              alt="#"
              height="100%"
              ref="mainImageRef"
              class="second"
            >
              <template v-slot:placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <ai-circle-loader />
                </div>
              </template>
            </v-img>
            <v-img v-show="imageTab === 'before'" :src="imageForActions" alt="#" height="100%" class="second">
              <template v-slot:placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <ai-circle-loader />
                </div>
              </template>
            </v-img>
          </div>
        </div>
        <div class="result-image-controller" :class="{'result-image-controller--mobile': isMobile}">
          <ai-button theme="dark" @click="imageResult = ''">{{ $t('common.back') }}</ai-button>
          <ai-button theme="dark" @click="toEditHadler">{{ $t('common.edit') }}</ai-button>
          <ai-button
            theme="dark"
            @click="
              imagePrev = '';
              imageForActions = '';
              imageResult = '';
              generateImageData.products = [];
            "
            >{{ $t('services.reset') }}</ai-button
          >
          <ai-button v-if="servicesList.length > 1" theme="dark" @click="otherServiceModal = true">
            {{ $t('services.open-in') }}
          </ai-button>
          <ai-button theme="gradient-primary" class="result-image-controller__button" @click.stop="downloadHandler">
            <inline-svg :src="require('@/assets/icons/actions/download-with-line.svg')" />
            {{ !isMobile ? $t('common.download') : '' }}
          </ai-button>
        </div>
      </div>
    </div>
    <image-finetune
      v-else
      :src="imageResult"
      @reset="resetHandler"
      @save="editHandler"
      :actions="['reset', 'save']"
    />
    <ai-bottom-modal
      :show-modal="otherServiceModal"
      :title="$t('services.open-modal-title')"
      :handle-toggle="otherServiceModalCloseHandler"
      no-height
    >
      <div v-if="loading" class="overlay-loading">
        <ai-circle-loader />
      </div>
      <div class="services__list">
        <ai-clear-button
          v-for="(service, index) in computedMenuList"
          :key="index"
          @click="goToServiceHandler(service.service)"
          class="services__list__item"
        >
          <div class="services__list__item__icon">
            <inline-svg :src="service.icon" />
          </div>
          <div class="services__list__item__title">
            {{ service.title }}
          </div>
        </ai-clear-button>
      </div>
    </ai-bottom-modal>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ImgComparisonSlider } from '@img-comparison-slider/vue';

import { downloadImage } from '@/utils/downloadImage';

import { TService } from '@/types/TService';
import { TTabItem } from '@/types/TTabItem';
import { IMultiMenuItem } from '@/types/IMultiMenuItem';

import { useCommonStore } from '@/store/common';
import { useEditorStore } from '@/store/editor';
import { useServicesStore } from '@/store/services';

import AiBottomModal from '@/components/UI/AiBottomModal/AiBottomModal.vue';
import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCircleLoader from '@/components/UI/AiCircleLoader/AiCircleLoader.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiTabs from '@/components/UI/AiTabs/AiTabs.vue';

import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';

const { t } = useI18n();

const { isMobile, currentService, servicesList } = storeToRefs(useCommonStore());
const { imageForActions, imagePrev, imageResult, loading } = storeToRefs(useServicesStore());
const { generateImageData } = storeToRefs(useEditorStore());

const imageTab = ref<string>('');
const mainImageRef = ref<any | null>(null);
const imageWidth = ref<number>(1);
const imageHeight = ref<number>(1);
const otherServiceModal = ref<boolean>(false);
const isEdit = ref<boolean>(false);
const imageTabs = ref<TTabItem[]>([
  {
    label: t('services.comparison'),
    value: 'comparison',
  },
  {
    label: t('services.before'),
    value: 'before',
  },
  {
    label: t('services.after'),
    value: 'after',
  },
]);

const localServicesList = ref<any>([
  {
    title: t('multi-menu.market-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/market.svg'),
    service: 'create_background_for_image',
  },
  {
    title: t('multi-menu.remove-background-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/remove-background.svg'),
    service: 'remove_background',
  },
  {
    title: t('multi-menu.infographics-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/infographics.svg'),
    service: 'infographics',
  },
  {
    title: t('multi-menu.upscale-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/upscale.svg'),
    service: 'upscale',
  },
  // {
  //   title: t('multi-menu.watermarks-title'),
  //   // eslint-disable-next-line global-require
  //   icon: require('@/assets/icons/multi-menu/watermarks.svg'),
  //   service: 'Watermarks',
  // },
  {
    title: t('multi-menu.outpaint-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/outpaint.svg'),
    service: 'outpaint',
  },
  {
    title: t('multi-menu.inpaint-title'),
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/inpaint.svg'),
    service: 'inpaint',
  },
  // {
  //   title: t('multi-menu.shadow-title'),
  //   // eslint-disable-next-line global-require
  //   icon: require('@/assets/icons/multi-menu/shadow.svg'),
  //   service: 'Shadow',
  // },
]);

// eslint-disable-next-line max-len
const computedMenuList = computed(() => localServicesList.value.filter((menuItem: IMultiMenuItem) => servicesList.value.find((serviceItem: TService) => menuItem.service === serviceItem)));

const downloadHandler = (): void => {
  const ext = imageResult.value.substring('data:image/'.length, imageResult.value.indexOf(';base64'));
  downloadImage({
    url: imageResult.value,
    name: `result.${ext}`,
  });
};

const goToServiceHandler = (name: TService): void => {
  imageForActions.value = imageResult.value;
  imageResult.value = '';
  currentService.value = name;
  if (name === 'create_background_for_image') {
    localStorage.setItem('step', '3');
  } else if (name === 'remove_background') {
    localStorage.setItem('to-remove-bg', 'true');
  }
};

const otherServiceModalCloseHandler = (): void => {
  otherServiceModal.value = false;
};

const imageSizeHandler = (): void => {
  if (imageForActions.value !== '') {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
    };
    img.src = imageForActions.value;
  }
};

const toEditHadler = ():void => {
  isEdit.value = true;
};

const resetHandler = (): void => {
  isEdit.value = false;
};

const editHandler = async (image: string): Promise<void> => {
  imageResult.value = image;
  isEdit.value = false;
};

onMounted(() => {
  imageSizeHandler();
});
</script>

<style scoped lang="scss" src="./ResultImage.scss" />
